'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { Check, Minus, ChevronDown, ArrowRight, Phone, Users, Zap, Shield, type LucideIcon } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { PRICING_CONTENT } from '@/core/i18n/content/pricing.content';
import { PRICING_MATRIX_CONTENT } from '@/core/i18n/content/pricing-matrix.content';
import type { PricingContent } from '@/core/i18n/content/pricing.types';
import type { PricingPlan } from '../types/hotels.types';
import { PRICING_PLANS } from './hotels.data';


/* -------------------------------------------------------------------------- */
/*  Feature Comparison Matrix Data                                            */
/* -------------------------------------------------------------------------- */

/* Structural tier data (booleans only — labels come from content dictionary) */
const MATRIX_TIERS: Array<{ starter: boolean; professional: boolean; enterprise: boolean }[]> = [
  /* Core PMS */
  [{ starter: true, professional: true, enterprise: true }, { starter: true, professional: true, enterprise: true }, { starter: true, professional: true, enterprise: true }, { starter: true, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }],
  /* Revenue & Analytics */
  [{ starter: true, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: false, enterprise: true }, { starter: false, professional: false, enterprise: true }, { starter: false, professional: false, enterprise: true }],
  /* Guest Management */
  [{ starter: true, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: false, enterprise: true }, { starter: false, professional: true, enterprise: true }],
  /* F&B & POS */
  [{ starter: false, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: false, enterprise: true }],
  /* Compliance */
  [{ starter: true, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }],
  /* Support */
  [{ starter: true, professional: true, enterprise: true }, { starter: false, professional: true, enterprise: true }, { starter: false, professional: false, enterprise: true }, { starter: false, professional: false, enterprise: true }, { starter: false, professional: false, enterprise: true }],
  /* AI & Intelligence */
  [{ starter: false, professional: true, enterprise: true }, { starter: false, professional: false, enterprise: true }, { starter: false, professional: false, enterprise: true }, { starter: false, professional: false, enterprise: true }, { starter: false, professional: false, enterprise: true }],
];

const ENTERPRISE_ICONS: LucideIcon[] = [Users, Zap, Shield, Phone];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function computePrice(priceStr: string, isAnnual: boolean): string {
  const num = parseInt(priceStr.replace(/,/g, ''), 10);
  if (isAnnual) return Math.round(num * 0.8).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return priceStr;
}

/* -------------------------------------------------------------------------- */
/*  PricingPage — Main                                                        */
/* -------------------------------------------------------------------------- */

export function PricingPage() {
  const c = usePageContent(PRICING_CONTENT);
  const m = usePageContent(PRICING_MATRIX_CONTENT);
  const [isAnnual, setIsAnnual] = useState(false);
  const handleToggle = useCallback(() => setIsAnnual((p) => !p), []);

  return (
    <main id="pricing-page">
      {/* Hero */}
      <section id="pricing-page-hero" className="relative w-full overflow-hidden bg-hms-bg">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }} />
        <div className="relative mx-auto max-w-4xl px-4 pb-8 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
          <h1 id="pricing-page-title" className={cn('font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}>
            {c.hero.title}
          </h1>
          <p id="pricing-page-subtitle" className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">{c.hero.subtitle}</p>
        </div>
      </section>

      {/* Toggle + Plan Cards */}
      <section id="pricing-page-plans" className="relative w-full overflow-hidden bg-hms-bg pb-20">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <PricingToggle isAnnual={isAnnual} onToggle={handleToggle} c={c} />
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {PRICING_PLANS.map((plan, idx) => (
              <PricingCard key={plan.name} plan={plan} index={idx} isAnnual={isAnnual} c={c} planName={c.plans.names[idx] ?? plan.name} planTagline={c.plans.taglines[idx] ?? plan.tagline} planFeatures={c.plans.features[idx] ?? plan.features} />
            ))}
          </div>
        </div>
      </section>

      <FeatureMatrix c={c} m={m} />
      <EnterpriseSection c={c} />
      <PricingFAQSection c={c} />
      <PricingCTA c={c} />
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/*  Pricing Toggle                                                            */
/* -------------------------------------------------------------------------- */

function PricingToggle({ isAnnual, onToggle, c }: { isAnnual: boolean; onToggle: () => void; c: PricingContent }) {
  return (
    <div id="pricing-page-toggle" className="flex items-center justify-center gap-3">
      <span id="pricing-toggle-monthly" className={cn('cursor-pointer text-sm font-semibold transition-colors', !isAnnual ? 'text-hms-gold' : 'text-hms-textDim')} onClick={onToggle} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onToggle(); }}>
        {c.toggle.monthly}
      </span>
      <button id="pricing-page-toggle-btn" type="button" onClick={onToggle} className={cn('relative inline-flex h-7 w-14 shrink-0 items-center rounded-full border-2 transition-colors', isAnnual ? 'border-hms-gold bg-hms-gold/20' : 'border-hms-border bg-hms-surface')} aria-label={c.toggle.ariaToggle}>
        <span className={cn('inline-block h-5 w-5 rounded-full bg-hms-gold shadow-sm transition-transform', isAnnual ? 'translate-x-7' : 'translate-x-0.5')} />
      </button>
      <span id="pricing-toggle-annual" className={cn('cursor-pointer text-sm font-semibold transition-colors', isAnnual ? 'text-hms-gold' : 'text-hms-textDim')} onClick={onToggle} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onToggle(); }}>
        {c.toggle.annual}
      </span>
      {isAnnual && (
        <span className="rounded-full bg-hms-green/10 px-2.5 py-0.5 text-xs font-semibold text-hms-green">{c.toggle.save}</span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Pricing Card                                                              */
/* -------------------------------------------------------------------------- */

function PricingCard({ plan, index, isAnnual, c, planName, planTagline, planFeatures }: { plan: PricingPlan; index: number; isAnnual: boolean; c: PricingContent; planName: string; planTagline: string; planFeatures: string[] }) {
  const isPopular = plan.popular;
  const displayPrice = computePrice(plan.price, isAnnual);

  return (
    <div id={`pricing-plan-${index}`} className={cn('relative flex flex-col rounded-xl border bg-hms-card p-8', 'transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20', isPopular ? 'border-hms-gold shadow-md shadow-hms-gold/10' : 'border-hms-border hover:border-hms-gold/20')}>
      {isPopular && (
        <span id="pricing-page-popular-badge" className={cn('absolute -top-3.5 left-1/2 -translate-x-1/2', 'rounded-full bg-hms-gold px-4 py-1 text-xs font-bold tracking-wide text-hms-bg')}>{c.card.popular}</span>
      )}
      <h3 className="text-lg font-bold text-hms-text">{planName}</h3>
      <p className="mt-1 text-sm text-hms-textSub">{planTagline}</p>
      <div className="mt-6 flex items-baseline gap-1">
        <span className="text-sm font-medium text-hms-textDim">{c.card.currency}</span>
        <span className="text-4xl font-extrabold tracking-tight text-hms-text">{displayPrice}</span>
        <span className="text-sm text-hms-textDim">{c.card.perMonth}</span>
      </div>
      {isAnnual && (
        <p className="mt-1 text-xs text-hms-textDim">{c.card.billedAnnually} ({c.card.currency} {computePrice(plan.price, false)} {c.toggle.monthly.toLowerCase()})</p>
      )}
      <div className="my-6 h-px w-full bg-hms-border" />
      <ul className="flex-1 space-y-3">
        {planFeatures.map((feature, fIdx) => (
          <li key={fIdx} className="flex items-start gap-3">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-hms-green" />
            <span className="text-sm leading-snug text-hms-textSub">{feature}</span>
          </li>
        ))}
      </ul>
      <LocaleLink id={`pricing-page-cta-${index}`} href="/register-hotel" className={cn('mt-8 flex items-center justify-center rounded-lg px-6 py-3', 'text-sm font-semibold tracking-wide transition-all duration-200', isPopular ? 'bg-hms-gold text-hms-bg hover:bg-hms-goldLt' : 'border border-hms-border text-hms-text hover:border-hms-gold/40 hover:text-hms-gold')}>
        {c.card.trialBtn}
      </LocaleLink>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Feature Comparison Matrix                                                 */
/* -------------------------------------------------------------------------- */

function FeatureMatrix({ c, m }: { c: PricingContent; m: import('@/core/i18n/content/pricing-matrix.content').PricingMatrixContent }) {
  return (
    <section id="pricing-matrix-section" className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2 id="pricing-matrix-title" className={cn('text-center text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}>{c.matrix.title}</h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-base text-hms-textSub">{c.matrix.subtitle}</p>
        <div className="mt-12 overflow-x-auto">
          <table id="pricing-matrix-table" className="w-full min-w-[600px] border-collapse">
            <thead>
              <tr className="border-b border-hms-border">
                <th className="pb-4 text-left text-sm font-semibold text-hms-text">{c.matrix.feature}</th>
                <th className="pb-4 text-center text-sm font-semibold text-hms-text">{c.matrix.starter}</th>
                <th className="pb-4 text-center text-sm font-semibold text-hms-gold">{c.matrix.professional}</th>
                <th className="pb-4 text-center text-sm font-semibold text-hms-text">{c.matrix.enterprise}</th>
              </tr>
            </thead>
            <tbody>
              {m.categories.map((cat, catIdx) => (
                <MatrixBlock key={catIdx} category={cat} featureNames={m.features[catIdx] ?? []} tiers={MATRIX_TIERS[catIdx] ?? []} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/**
 * "Core PMS" -> "core-pms", "Revenue & Analytics" -> "revenue---analytics".
 *
 * Lowercase, then every non-alphanumeric run becomes a single dash per
 * character, which is what the existing selectors expect
 * (`revenue---analytics` keeps three dashes for " & ").
 */
function categorySlug(category: string): string {
  return category.toLowerCase().replace(/[^a-z0-9]/g, '-');
}

function MatrixBlock({ category, featureNames, tiers }: { category: string; featureNames: string[]; tiers: Array<{ starter: boolean; professional: boolean; enterprise: boolean }> }) {
  return (
    <>
      <tr>
        {/* Slug, not index.
            `pricing-matrix-cat-${catIdx}` renumbered every category whenever one
            was inserted or reordered, so any selector pinned to it silently
            pointed at a different row. A slug derived from the category name is
            stable under reordering and readable in a failing assertion. */}
        <td id={`pricing-matrix-cat-${categorySlug(category)}`} colSpan={4} className="pb-2 pt-6 text-xs font-bold uppercase tracking-widest text-hms-gold">{category}</td>
      </tr>
      {featureNames.map((name, rowIdx) => {
        const row = tiers[rowIdx];
        return (
          <tr key={rowIdx} className="border-b border-hms-border/50">
            <td className="py-3 text-sm text-hms-textSub">{name}</td>
            {(['starter', 'professional', 'enterprise'] as const).map((tier) => (
              <td key={tier} className="py-3 text-center">
                {row?.[tier] ? <Check className="mx-auto h-4 w-4 text-hms-green" /> : <Minus className="mx-auto h-4 w-4 text-hms-textDim" />}
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Enterprise Section                                                        */
/* -------------------------------------------------------------------------- */

function EnterpriseSection({ c }: { c: PricingContent }) {
  return (
    <section id="pricing-page-enterprise" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-hms-gold/20 bg-hms-card p-8 sm:p-12">
          <h2 id="pricing-page-enterprise-title" className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl">{c.enterprise.title}</h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-sm text-hms-textSub">{c.enterprise.subtitle}</p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.enterprise.perks.map((perk, idx) => {
              const Icon = ENTERPRISE_ICONS[idx] ?? Users;
              return (
                <div key={idx} className="flex items-center gap-3 rounded-lg border border-hms-border bg-hms-surface p-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-hms-gold/10">
                    <Icon className="h-4 w-4 text-hms-gold" />
                  </div>
                  <span className="text-sm font-medium text-hms-textSub">{perk}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-8 text-center">
            <LocaleLink id="pricing-page-enterprise-cta" href="/contact" className={cn('inline-flex items-center gap-2 rounded-lg border border-hms-gold px-8 py-3', 'text-sm font-semibold tracking-wide text-hms-gold', 'transition-all duration-200 hover:bg-hms-gold hover:text-hms-bg')}>
              {c.enterprise.contactBtn}
              <ArrowRight className="h-4 w-4" />
            </LocaleLink>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  FAQ Section                                                               */
/* -------------------------------------------------------------------------- */

function PricingFAQSection({ c }: { c: PricingContent }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id="pricing-faq-section" className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2 id="pricing-faq-title" className={cn('text-center text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}>{c.faq.title}</h2>
        <div className="mt-12 space-y-3">
          {c.faq.items.map((item, idx) => (
            <div key={idx} id={`pricing-faq-item-${idx}`} className={cn('rounded-xl border transition-colors', openIndex === idx ? 'border-hms-gold/30 bg-hms-card' : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15')}>
              <button id={`pricing-faq-toggle-${idx}`} type="button" onClick={() => toggle(idx)} className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left" aria-expanded={openIndex === idx}>
                <span className={cn('text-sm font-semibold leading-snug', openIndex === idx ? 'text-hms-gold' : 'text-hms-text')}>{item.question}</span>
                <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform duration-200', openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim')} />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Final CTA                                                                 */
/* -------------------------------------------------------------------------- */

function PricingCTA({ c }: { c: PricingContent }) {
  return (
    <section id="pricing-page-cta-section" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }} />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2 id="pricing-page-final-cta-title" className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent')}>{c.cta.title}</h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">{c.cta.subtitle}</p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink id="pricing-page-final-cta-trial" href="/register-hotel" className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20', 'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]', 'w-full sm:w-auto justify-center')}>
            {c.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink id="pricing-page-final-cta-contact" href="/contact" className={cn('inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-text', 'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold', 'w-full sm:w-auto')}>
            {c.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">{c.cta.disclaimer}</p>
      </div>
    </section>
  );
}
